'use client';

import { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Miembro } from '@/data/miembros/miembrosData';

interface TeamSectionProps {
  miembros: Miembro[];
}

function MiembroCard({ miembro }: { miembro: Miembro }) {
  const [abierto, setAbierto] = useState(false);
  const IconComponent = miembro.icon;

  return (
    <Box
      sx={{
        // Ancho responsivo: 100% móvil, casi 50% tablet, casi 25% escritorio
        width: { 
          xs: '100%', 
          sm: 'calc(50% - 16px)', 
          md: 'calc(25% - 18px)' 
        },
        border: '1px solid',
        borderColor: abierto ? alpha(miembro.color, 0.4) : alpha('#3b82f6', 0.1),
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        boxShadow: abierto ? `0 8px 24px ${alpha(miembro.color, 0.12)}` : 'none',
        bgcolor: 'background.paper',
        alignSelf: 'flex-start', // CRUCIAL: Evita que la tarjeta se estire si su vecina se abre
      }}
    >
      {/* ── Cabecera ── */}
      <Box
        onClick={() => setAbierto(!abierto)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          bgcolor: abierto ? alpha(miembro.color, 0.04) : 'transparent',
          '&:hover': { bgcolor: alpha(miembro.color, 0.06) },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: alpha(miembro.color, 0.1),
            color: miembro.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: alpha(miembro.color, 0.2),
          }}
        >
          <IconComponent sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.9rem', // Tamaño ajustado (ni muy grande ni muy pequeño)
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {miembro.nombre}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: miembro.color,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {miembro.puesto}
          </Typography>
        </Box>

        <IconButton
          size="small"
          sx={{
            transform: abierto ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Desplegable (Crecimiento Vertical Puro) ── */}
      <Collapse in={abierto} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: 2,
            pb: 3,
            pt: 1,
            borderTop: '1px solid',
            borderColor: alpha(miembro.color, 0.08),
            bgcolor: alpha(miembro.color, 0.01),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 1 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                bgcolor: alpha(miembro.color, 0.08),
                color: miembro.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconComponent sx={{ fontSize: 24 }} />
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{ 
              color: 'text.secondary', 
              lineHeight: 1.5, 
              textAlign: 'center', 
              fontSize: '0.85rem',
              fontStyle: 'italic',
            }}
          >
            "{miembro.descripcion}"
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

export default function TeamSection({ miembros }: TeamSectionProps) {
  return (
    <Box sx={{ mb: 10 }}>
      <Typography
        variant="h4"
        sx={{ 
          fontWeight: 800, 
          textAlign: 'center', 
          mb: 5, 
          color: 'text.primary',
        }}
      >
      </Typography>

      {/* Contenedor Flex con Wrap para evitar el estiramiento horizontal */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          justifyContent: 'center',
          alignItems: 'flex-start' // Asegura que cada tarjeta crezca a su ritmo
        }}
      >
        {miembros.map((miembro, index) => (
          <MiembroCard key={index} miembro={miembro} />
        ))}
      </Box>
    </Box>
  );
}